/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';

import 'graphiql/graphiql.min.css';
import axios from 'axios';
import base64url from 'base64url';
import classNames from 'classnames';
import * as crypto from 'crypto';
import { format } from 'date-fns-tz';
import GraphiQL from 'graphiql';
import qs from 'qs';

import { MuiThemeProvider } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import LogOutIcon from './assets/img/logout.svg';
import OauthPopup from './components/Popup/Popup';
import { PreLoaderLogo } from './components/PreLoaderLogo/PreLoaderLogo';
import { asgardeoConfig, insightsAPIUrl, tokenExchangeConfig, validateUserAPIUrl } from './config';
import { theme } from './theme/theme';

const useStyles = () => ({
    root: {
        height: '100%'
    },
    primaryBtn: {
        textTransform: 'capitalize',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontSize: 13,
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px 0 rgba(0,0,0,0.15)',
        '&:hover': {
            backgroundColor: '#5464D0',
            color: theme.palette.common.white,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06), 0 2px 4px 0 rgba(0,0,0,0.07)'
        },
        '&:focus': {
            backgroundColor: theme.palette.primary.main,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0) 100%)',
            color: theme.palette.common.white,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08), 0 0 0 0 rgba(0,0,0,0.07)'
        },
        '&:active': {
            backgroundColor: theme.palette.primary.main
        }
    },
    logInBtn: {
        height: theme.spacing(5),
        width: theme.spacing(11),
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        marginLeft: theme.spacing(1.25)
    },
    rounded: {
        height: theme.spacing(5),
        borderRadius: theme.spacing(3.0)
    },
    loggedInExplorer: {
        height: '100%'
    },
    loggedOutExplorer: {
        height: '100%',
        filter: 'opacity(50%)',
        'pointer-events': 'none'
    },
    tableObject: {
        padding: theme.spacing(2)
    },
    tableObjectItem: {
        display: 'table-cell',
        width: '1%',
        'white-space': 'nowrap',
        'vertical-align': 'middle'
    },
    navBarAvatar: {
        width: theme.spacing(4) + 'px !important',
        height: theme.spacing(4) + 'px !important',
        marginRight: theme.spacing(1.5)
    },
    userText: {
        color: theme.palette.grey[500] + ' !important',
        textTransform: 'capitalize',
        lineHeight: 1.4 + ' !important',
        flexShrink: 0,
        [theme.breakpoints.up('xs')]: {
            display: 'none'
        },
        [theme.breakpoints.up('lg')]: {
            display: 'block'
        }
    },
    userBtn: {
        '&:hover': {
            backgroundColor: 'transparent'
        }
    },
    downArrowIcon: {
        fontSize: '1rem',
        marginLeft: theme.spacing(0.5),
        color: theme.palette.grey[500]
    },
    userMenuItem: {
        cursor: 'default !important',
        pointerEvents: 'none',
        paddingLeft: theme.spacing(3) + 'px !important',
        paddingRight: theme.spacing(3) + 'px !important',
        paddingBottom: theme.spacing(1) + 'px !important',
        paddingTop: theme.spacing(2) + 'px !important'
    },
    userDetails: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
    },
    menuAvatar: {
        width: theme.spacing(6) + 'px !important',
        height: theme.spacing(6) + 'px !important'
    },
    emailText: {
        color: theme.palette.grey[400] + ' !important',
        lineHeight: 1.6 + ' !important'
    },
    menuPaper: {
        marginTop: theme.spacing(3) + 'px !important',
        boxShadow: 'inset 0 -1px 0 0 #D3D4DD, 0 5px 50px 0 rgba(102,103,133,0.27) !important',
        borderRadius: theme.spacing(0.75) + 'px !important'
    },
    userItem: {
        paddingLeft: theme.spacing(3) + 'px !important',
        paddingRight: theme.spacing(3) + 'px !important',
        paddingBottom: theme.spacing(2) + 'px !important',
        paddingTop: theme.spacing(2) + 'px !important',
        '&:hover': {
            backgroundColor: "#F4F5F7 !important'"
        }
    },
    itemSecondaryText: {
        color: theme.palette.primary.contrastText + ' !important',
        marginLeft: theme.spacing(2) + 'px !important',
        marginTop: theme.spacing(0.25) + 'px !important'
    },
    preLoaderContainer: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'fixed',
        backgroundColor: theme.palette.secondary.light,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1201
    }
});

const authCodeURL =
    asgardeoConfig.endpoints.authorizationEndpoint +
    '?response_type=code&client_id=' +
    asgardeoConfig.clientID +
    '&redirect_uri=' +
    asgardeoConfig.signInRedirectURL +
    '&scope=openid%20email%20profile' +
    '&response_mode=query';

const defaultQuery =
    `query ($dataFilter: DataFilter!, $filter: TimeFilter!) {\n` +
    `  getTotalTraffic(filter: $filter, dataFilter: $dataFilter)\n` +
    `  getOverallLatency(filter: $filter, dataFilter: $dataFilter)\n` +
    `  getTotalErrors(filter: $filter, dataFilter: $dataFilter)\n` +
    `}`;

class App extends React.Component {
    constructor(props) {
        super(props);

        const UTC_BROWSER_LOCALE_FORMAT = "yyy-MM-dd'T'HH:mm:ss.SSSXXX";
        let to = new Date();
        to.setMilliseconds(0);
        let from = new Date(to.getTime());
        from = new Date(from.setDate(from.getDate() - 7));
        from = format(from, UTC_BROWSER_LOCALE_FORMAT);
        to = format(to, UTC_BROWSER_LOCALE_FORMAT);

        this.state = {
            fromDate: from,
            toDate: to,
            exchangedToken: '',
            asgardeoIdToken: '',
            userName: '',
            userProfilePictureUrl: '',
            userDefaultOrg: '',
            userOrganizations: '',
            userLoggedIn: false,
            userEmail: '',
            anchorEl: null,
            loginPending: false,
            logoutPending: false
        };
        this.onCode = this.onCode.bind(this);
        this.onClose = this.onClose.bind(this);
        this.simulateClick = this.simulateClick.bind(this);
        this.graphQLFetcher = this.graphQLFetcher.bind(this);
        this.setUserDetails = this.setUserDetails.bind(this);
        this.clearUserDetails = this.clearUserDetails.bind(this);
        this.getDefaultVariables = this.getDefaultVariables.bind(this);
        this.validateUserAndPerformTokenExchange = this.validateUserAndPerformTokenExchange.bind(this);
    }

    getDefaultVariables() {
        const { fromDate, toDate, userLoggedIn, userDefaultOrg } = this.state;
        if (userLoggedIn && userDefaultOrg) {
            const orgId = userDefaultOrg.uuid;
            return (
                '{\n' +
                '  "filter": {\n' +
                '    "from": "' +
                fromDate +
                '",\n' +
                '    "to": "' +
                toDate +
                '"\n' +
                '  },\n' +
                '  "latencySummaryFilter": {\n' +
                '    "granularity": "1d"\n' +
                '  },\n' +
                '  "granularity": "1d",\n' +
                '  "dataFilter": {\n' +
                '    "orgId": "' +
                orgId +
                '",\n' +
                '    "environmentId": "3b7276f2-6bd7-4f14-91d0-6110fd894787",\n' +
                '    "tenant": "carbon.super"\n' +
                '  }\n' +
                '}'
            );
        } else {
            return (
                '{\n' +
                '  "filter": {\n' +
                '    "from": "' +
                fromDate +
                '",\n' +
                '    "to": "' +
                toDate +
                '"\n' +
                '  },\n' +
                '  "latencySummaryFilter": {\n' +
                '    "granularity": "1d"\n' +
                '  },\n' +
                '  "granularity": "1d",\n' +
                '  "dataFilter": {\n' +
                '    "orgId": "12345678-1234-4321-1234-123456789012",\n' +
                '    "environmentId": "production",\n' +
                '    "tenant": "carbon.super"\n' +
                '  }\n' +
                '}'
            );
        }
    }

    setUserDetails(data, defaultOrg, exchangedToken, asgardeoIdToken) {
        this.setState({
            loginPending: false,
            logoutPending: false,
            userLoggedIn: true,
            exchangedToken: exchangedToken,
            asgardeoIdToken: asgardeoIdToken,
            userName: data.displayName,
            userEmail: data.userEmail,
            userDefaultOrg: defaultOrg,
            userOrganizations: data.organizations,
            userProfilePictureUrl: data.userProfilePictureUrl
        });
    }

    clearUserDetails() {
        this.setState({
            userLoggedIn: false,
            exchangedToken: '',
            asgardeoIdToken: '',
            userName: '',
            userProfilePictureUrl: '',
            userDefaultOrg: '',
            userOrganizations: '',
            anchorEl: null,
            loginPending: false,
            logoutPending: false
        });
        localStorage.clear();
    }

    async graphQLFetcher(graphQLParams) {
        if (!this.state.userLoggedIn) {
            return '';
        } else {
            const { exchangedToken } = this.state;
            return await fetch(insightsAPIUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + exchangedToken
                },
                body: JSON.stringify(graphQLParams),
                credentials: 'include'
            }).then((response) => {
                if (!response.ok) {
                    this.clearUserDetails();
                }
                return response.json();
            });
        }
    }

    onCode = async (params, code_verifier) => {
        const code = params.get('code');
        await axios
            .post(
                asgardeoConfig.endpoints.tokenEndpoint,
                qs.stringify({
                    client_id: asgardeoConfig.clientID,
                    code: code,
                    grant_type: 'authorization_code',
                    redirect_uri: asgardeoConfig.signInRedirectURL,
                    code_verifier: code_verifier
                }),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8'
                    }
                }
            )
            .then((response) => {
                if (response && response.data) {
                    this.validateUserAndPerformTokenExchange(response.data);
                }
            })
            .catch((error) => {
                console.error('Error occurred while retrieving an asgardeo token', error);
            });
    };

    validateUserAndPerformTokenExchange = async (data) => {
        if (data && data.access_token && data.id_token) {
            const { access_token: asgardeoAccessToken, id_token: asgardeoIdToken } = data;
            await axios
                .get(validateUserAPIUrl, {
                    headers: {
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + asgardeoAccessToken
                    }
                })
                .then((response) => {
                    const { data } = response;
                    if (data && data.organizations && data.organizations.length > 0) {
                        const { organizations: userOrgList, displayName } = data;
                        const defaultOrg = userOrgList.find((org) => org.name === displayName) || userOrgList[0];
                        this.performTokenExchange(data, defaultOrg, asgardeoAccessToken, asgardeoIdToken);
                    } else {
                        console.error('No organizations found for the user');
                    }
                })
                .catch((error) => {
                    console.error('Error occurred while validating the user', error);
                });
        } else {
            console.error('Cannot find the access_token or id_token');
        }
    };

    performTokenExchange = async (validateUserResponse, defaultOrg, asgardeoAccessToken, asgardeoIdToken) => {
        if (defaultOrg && defaultOrg.handle) {
            await axios
                .post(
                    tokenExchangeConfig.tokenEndpoint,
                    qs.stringify({
                        ...tokenExchangeConfig.data,
                        subject_token: asgardeoAccessToken,
                        orgHandle: defaultOrg.handle
                    }),
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8'
                        }
                    }
                )
                .then((response) => {
                    if (response.data && response.data.access_token) {
                        this.setUserDetails(
                            validateUserResponse,
                            defaultOrg,
                            response.data.access_token,
                            asgardeoIdToken
                        );
                    } else {
                        console.error('No access token found in the token exchange response');
                    }
                })
                .catch((error) => {
                    console.error('Error occurred while retrieving the exchanged token', error);
                });
        } else {
            console.error('Cannot find the default organization');
        }
    };

    onClose = () => console.debug('Popup window closed');

    handleLoginPending = () => {
        this.setState({
            loginPending: true
        });
    };

    simulateClick = (e) => {
        if (e) {
            e.click();
        }
    };

    render() {
        const { classes } = this.props;

        const userSignInOptions = (
            <div>
                <Typography variant='subtitle1' display={'inline'}>
                    Start exploring Choreo Insights API queries using your account’s data now.
                    <Button
                        classes={{
                            root: classNames(classes.primaryBtn, classes.rounded, classes.logInBtn)
                        }}
                        type='button'
                        id='user-login'
                        role='button'
                        onClick={this.handleLoginPending}
                    >
                        <Typography variant='h4'>Sign In</Typography>
                    </Button>
                </Typography>
            </div>
        );

        const userLabel = (
            <React.Fragment>
                <Avatar
                    alt={this.state.userName}
                    src={this.state.userProfilePictureUrl}
                    className={classNames('user-profile-pic', classes.navBarAvatar)}
                />
                <Typography variant='body1' className={classNames('user-profile-name', classes.userText)}>
                    {this.state.userName}
                </Typography>
            </React.Fragment>
        );

        const userName = (
            <MenuItem className={classes.userMenuItem}>
                <Avatar
                    alt={this.state.userName}
                    src={this.state.userProfilePictureUrl}
                    className={classNames('user-profile-pic', classes.menuAvatar)}
                />
                <div className={classes.userDetails}>
                    <Typography
                        variant='body1'
                        component='div'
                        className={classNames(classes.userText, 'user-profile-name')}
                    >
                        {this.state.userName}
                    </Typography>
                    <Typography
                        variant='body1'
                        component='div'
                        className={classNames(classes.emailText, 'user-profile-email')}
                    >
                        {this.state.userEmail}
                    </Typography>
                </div>
            </MenuItem>
        );

        const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
            this.setState({
                anchorEl: event.currentTarget
            });
        };

        const open = Boolean(this.state.anchorEl);

        const handleClose = () => {
            this.setState({
                anchorEl: null
            });
        };

        const handleLogout = async () => {
            const { asgardeoIdToken } = this.state;
            this.setState({
                logoutPending: true
            });
            const logOutURL =
                asgardeoConfig.endpoints.endSessionEndpoint +
                '?id_token_hint=' +
                asgardeoIdToken +
                '&post_logout_redirect_uri=' +
                asgardeoConfig.signOutRedirectURL +
                '&state=sign_out_success';
            await axios
                .get(logOutURL)
                .then(() => {
                    this.clearUserDetails();
                })
                .catch((error) => {
                    this.clearUserDetails();
                    console.error('Error occurred while logging out user', error);
                });
        };

        const logout = (
            <MenuItem className={classes.userItem} onClick={handleLogout} disableRipple={true}>
                <img src={LogOutIcon} alt={'logout'} />
                <Typography variant='body1' component='div' className={classes.itemSecondaryText}>
                    Logout
                </Typography>
            </MenuItem>
        );

        const userLogOutOptions = (
            <div>
                <React.Fragment>
                    <Button
                        aria-label='account of current user'
                        aria-controls='menu-appbar'
                        aria-haspopup='true'
                        onClick={handleMenu}
                        color='inherit'
                        id='current-user'
                        className={classes.userBtn}
                    >
                        {userLabel}
                        <KeyboardArrowDownIcon className={classes.downArrowIcon} />
                    </Button>
                    <Menu
                        id='menu-appbar'
                        anchorEl={this.state.anchorEl}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        keepMounted={true}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={open}
                        onClose={handleClose}
                        classes={{ paper: classes.menuPaper }}
                        getContentAnchorEl={null}
                    >
                        {userName}
                        {logout}
                    </Menu>
                </React.Fragment>
            </div>
        );

        let code_verifier = base64url(crypto.pseudoRandomBytes(32));
        let code_challenge = base64url(
            crypto
                .createHash('sha256')
                .update(code_verifier)
                .digest()
        );
        const pkceAuthCodeURL = authCodeURL + '&code_challenge_method=S256&code_challenge=' + code_challenge;

        const loginPending = (
            <div>
                <div className={classes.overlay} />
                <div className={classes.preLoaderContainer}>
                    <PreLoaderLogo size={'medium'} />
                </div>
                <OauthPopup
                    url={pkceAuthCodeURL}
                    onCode={this.onCode}
                    onClose={this.onClose}
                    height={500}
                    width={500}
                    code_verifier={code_verifier}
                >
                    <div ref={this.simulateClick} />;
                </OauthPopup>
            </div>
        );
        const logOutPending = (
            <div>
                <div className={classes.overlay} />
                <div className={classes.preLoaderContainer}>
                    <PreLoaderLogo size={'medium'} />
                </div>
            </div>
        );

        const loadingLogin = this.state.loginPending;
        const loadingLogOut = this.state.logoutPending;

        if (loadingLogin) {
            return loginPending;
        } else if (loadingLogOut) {
            return logOutPending;
        } else {
            return (
                <MuiThemeProvider theme={theme}>
                    <div className={classes.root}>
                        <div className={classes.tableObject}>
                            <div className={classes.tableObjectItem}>
                                <Typography variant='h2'>Choreo Insights API</Typography>
                            </div>
                            <div className={classes.tableObjectItem} align={'right'}>
                                {this.state.userLoggedIn ? userLogOutOptions : userSignInOptions}
                            </div>
                        </div>
                        <div className={this.state.userLoggedIn ? classes.loggedInExplorer : classes.loggedOutExplorer}>
                            <GraphiQL
                                fetcher={this.graphQLFetcher}
                                defaultQuery={defaultQuery}
                                variables={this.getDefaultVariables()}
                                docExplorerOpen={false}
                            />
                        </div>
                    </div>
                </MuiThemeProvider>
            );
        }
    }
}

export default withStyles(useStyles)(App);
